// Apolo IDE — Hermes worktree allocator: the .app adapter for the fan-out
// isolation port (WorktreeAllocator).
//
// Children may run in PARALLEL only when each has an ISOLATED root. This
// adapter carves one git worktree per child as a SIBLING of the parent root
// (`<parentRoot>-hermes-wt-<runId8>-<i>`), via the same Electron IPC the
// github-mention flow uses for git plumbing: window.apolo.shell.exec
// ({ command, cwd }) → { ok, exitCode, stdout, stderr }.
//
// Failure philosophy: isolation is an OPTIMIZATION, never a requirement. Any
// problem — no IPC (web build / tests), shell unavailable, parentRoot not a
// git repo, a worktree add failing midway — degrades to
// `{ isolated:false, roots:null }` so the caller falls back to driving the
// children SEQUENTIALLY in the shared parent root (zero clobber risk).
// `release` is best-effort cleanup and never throws.

// POSIX single-quote escaping: inside single quotes NOTHING expands ($, `,
// \, "), so the only character to handle is the quote itself — close, emit
// an escaped quote, reopen (' → '\''). The shell IPC runs commands through
// `/bin/bash -c`, so double quotes would NOT be safe for arbitrary paths.
const shq = (s) => `'${String(s).replace(/'/g, `'\\''`)}'`;

export function makeWorktreeAllocator({
  ipc = typeof window !== 'undefined' ? window.apolo : undefined,
} = {}) {
  const FALLBACK = { isolated: false, roots: null };

  // Shell run that returns the IPC result; never throws (same defensive shape
  // as useRemoteRunner's runShell).
  async function exec(command, cwd) {
    try {
      return await ipc.shell.exec({ command, cwd });
    } catch (e) {
      return { ok: false, exitCode: 1, stderr: e?.message || String(e) };
    }
  }

  /** Best-effort removal of every allocated worktree. NEVER throws. */
  async function release(allocations) {
    if (!ipc?.shell?.exec || !Array.isArray(allocations)) return;
    for (const a of allocations) {
      if (!a?.path) continue;
      await exec(`git worktree remove --force ${shq(a.path)}`, a.parentRoot);
    }
  }

  /**
   * Allocate `count` isolated roots for run `runId` under `parentRoot`.
   * Returns { isolated:true, roots, allocations } on success, or
   * { isolated:false, roots:null } whenever isolation is unavailable.
   */
  async function allocate(parentRoot, runId, count) {
    if (!ipc?.shell?.exec) return { ...FALLBACK };
    if (!parentRoot || !String(parentRoot).trim()) return { ...FALLBACK };
    if (!Number.isInteger(count) || count < 1) return { ...FALLBACK };

    // Trailing slashes would turn the "sibling" into a CHILD of the repo
    // ('/p/proj/' + '-hermes-wt-…' → '/p/proj/-hermes-wt-…'). Strip them.
    const root = String(parentRoot).replace(/\/+$/, '');
    if (!root) return { ...FALLBACK }; // parentRoot was the filesystem root

    const tag = String(runId ?? '').slice(0, 8);
    const allocations = [];
    const roots = [];
    for (let i = 0; i < count; i++) {
      const path = `${root}-hermes-wt-${tag}-${i}`;
      const res = await exec(`git worktree add ${shq(path)} HEAD`, root);
      if (!res?.ok) {
        console.warn(
          '[hermes] worktree allocation failed — falling back to serial-shared root:',
          res?.stderr || `exit ${res?.exitCode ?? '?'}`,
        );
        await release(allocations); // clean up whatever WAS created
        return { ...FALLBACK };
      }
      allocations.push({ path, parentRoot: root });
      roots.push(path);
    }
    return { isolated: true, roots, allocations };
  }

  return { allocate, release };
}
